import { Customization } from '@/common/types/student';
import Customizations from '@/game/GameComponents/GameData/customizations.json';
import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import { ScrollGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/ScrollGroup';
import { ScrollGroupItem } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/ScrollGroupItem';
import { waitForSeconds } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import ShopGridItem from '@/game/prefabs/CosmeticShop/ShopGridItem';
import { GridLayoutGroup, GridLayoutGroupConfig } from './GridLayoutGroup';
const customizations = Customizations as Customization[];

export interface ItemScrollGroupConfig {
  scene: Phaser.Scene;
  content: Phaser.GameObjects.Container;
  gridBacking: Phaser.GameObjects.Image | Phaser.GameObjects.NineSlice;
  scrollSpeedX?: number;
  scrollSpeedY?: number;
  scrollBacking?: Phaser.GameObjects.Image | Phaser.GameObjects.NineSlice;
  scrollHandle?: Phaser.GameObjects.Image | Phaser.GameObjects.NineSlice;
  loadingContainer?: Phaser.GameObjects.Container;
  loadingSpinner?: Phaser.GameObjects.Image;
  sortPredicate?: (a: ShopGridItem, b: ShopGridItem) => -1 | 0 | 1;
  showItemPrices?: boolean;
  gridLayoutConfig?: GridLayoutGroupConfig;
}

/*
  Scroll group specifically designed for cosmetic item grids.
*/
export class ItemScrollGroup {
  onUserClickedItem: (item: ShopGridItem) => void;
  itemGrid: GridLayoutGroup;
  scene: Phaser.Scene;
  content: Phaser.GameObjects.Container;
  loadingContainer?: Phaser.GameObjects.Container;
  loadingSpinner?: Phaser.GameObjects.Image;
  gridBacking: Phaser.GameObjects.Image | Phaser.GameObjects.NineSlice;
  scrollGroup: ScrollGroup;
  scrollBarBacking?: Phaser.GameObjects.Image | Phaser.GameObjects.NineSlice;
  scrollBarHandle?: Phaser.GameObjects.Image | Phaser.GameObjects.NineSlice;
  items: ShopGridItem[] = [];
  sortPredicate: (a: ShopGridItem, b: ShopGridItem) => -1 | 0 | 1;
  showItemPrices: boolean = true;
  isLoading: boolean = false;

  constructor(scrollGroupConfig: ItemScrollGroupConfig) {
    this.scene = scrollGroupConfig.scene;
    this.content = scrollGroupConfig.content;
    this.loadingContainer = scrollGroupConfig.loadingContainer;
    this.loadingSpinner = scrollGroupConfig.loadingSpinner;
    this.gridBacking = scrollGroupConfig.gridBacking;
    this.scrollBarBacking = scrollGroupConfig.scrollBacking;
    this.scrollBarHandle = scrollGroupConfig.scrollHandle;
    this.onUserClickedItem = () => {};
    if (scrollGroupConfig.showItemPrices !== undefined)
      this.showItemPrices = scrollGroupConfig.showItemPrices;

    // By default, sort items by rarity.
    const defaultSortPredicate = (a: ShopGridItem, b: ShopGridItem) => {
      if (a.cosmetic && b.cosmetic)
        return a.cosmetic.tier! > b.cosmetic.tier! ? 1 : -1;
      return 0;
    };
    this.sortPredicate =
      scrollGroupConfig.sortPredicate || defaultSortPredicate;

    const defaultLayoutSettings = {
      scene: this.scene,
      elements: [],
      columns: 4,
      hPadding: 140,
      vPadding: 182,
      hMargin: 28,
      vMargin: 32,
      backingElement: this.gridBacking,
    };

    this.itemGrid = new GridLayoutGroup({
      ...defaultLayoutSettings,
      ...scrollGroupConfig.gridLayoutConfig,
    });
    this.scene.add.existing(this.itemGrid);
    this.content.add(this.itemGrid);

    // Hide loading text.
    this.loadingContainer?.setVisible(false);
    if (this.loadingSpinner) {
      this.scene.tweens
        .add({
          targets: this.loadingSpinner,
          rotation: Math.PI * 2,
          duration: 2000,
          ease: 'Linear',
          repeat: -1,
        })
        .play();
    }

    this.scrollGroup = new ScrollGroup({
      scrollElement: this.gridBacking,
      scrollBacking: this.scrollBarBacking,
      scrollHandle: this.scrollBarHandle,
      scrollSpeedX: 0,
      maskMargins: {
        left: 4,
        right: 4,
        top: 4,
        bottom: 8,
      },
      ...scrollGroupConfig,
    });

    this.updateInteractivityWindows();
    this.scene.scale.on('resize', this.updateInteractivityWindows, this);
    ResponsivenessManager.InitializeResizeHandler(
      this.scene,
      () => this.updateInteractivityWindows(),
      this,
    );
    this.scene.events.once('destroy', () =>
      this.scene.scale.off('resize', this.updateInteractivityWindows, this),
    );
    this.scene.events.once('shutdown', () =>
      this.scene.scale.off('resize', this.updateInteractivityWindows, this),
    );
  }

  async initialize(
    onUserClickedItem: (item: ShopGridItem) => void,
    _initialFilter?: (c: Customization) => boolean,
  ): Promise<string> {
    this.onUserClickedItem = onUserClickedItem;

    await this.setItemFilter(_initialFilter || (() => true));

    return 'complete';
  }

  async setItemFilter(
    filter: (c: Customization) => boolean,
    onScrollGroupUpdated?: () => void,
  ) {
    this.items.forEach((item) => {
      item.setVisible(false);
    });
    let displayCustomizations = customizations.filter((c) => filter(c));
    // Remove items that are disabled.
    displayCustomizations = displayCustomizations.filter(
      (c) => c.disable !== true,
    );

    let visibleItems: Phaser.GameObjects.Container[] = [];

    // Show loading state immediately
    const hasItemsToLoad = displayCustomizations.some(
      (customization) =>
        !this.items.find((item) => item.cosmetic?.id === customization.id),
    );

    if (hasItemsToLoad) {
      this.loadingContainer?.setVisible(true);
      this.isLoading = true;
    }

    // First, update visibility of existing items - this should happen immediately
    this.items.forEach((item) => {
      const visible =
        displayCustomizations.find((c) => c.id === item.cosmetic?.id) !==
        undefined;
      item.setVisible(visible);
      if (visible) visibleItems.push(item);
    });

    // Update the grid with existing items immediately
    if (this.itemGrid) {
      this.sortItems();
      this.itemGrid.elements = this.items.filter((i) => i.visible);
      this.itemGrid.updateLayout();
      if (this.scrollGroup) {
        this.scrollGroup.updateContentParameters();
        this.scrollGroup.backToTop();
      }
    }

    // Find new customizations to add
    const newCustomizationsToAdd: Customization[] = [];
    displayCustomizations.forEach((customization) => {
      if (!this.items.find((item) => item.cosmetic?.id === customization.id)) {
        newCustomizationsToAdd.push(customization);
      }
    });

    // Load new items in background without blocking
    if (newCustomizationsToAdd.length > 0) {
      // Don't await here - let it run in background
      this.processBatchedItems(
        newCustomizationsToAdd,
        displayCustomizations,
        1,
        0,
        onScrollGroupUpdated,
      );
    } else if (hasItemsToLoad) {
      // No new items to load but loading text was shown
      this.loadingContainer?.setVisible(false);
      onScrollGroupUpdated?.();
    }
  }

  // New method to process items in batches with yields to browser
  async processBatchedItems(
    itemsToCreate: Customization[],
    displayCustomizations: Customization[],
    batchSize = 1,
    startIndex = 0,
    onFinished?: () => void,
  ) {
    // Process a small batch
    const endIndex = Math.min(startIndex + batchSize, itemsToCreate.length);
    const batch = itemsToCreate
      .slice(startIndex, endIndex)
      .filter((c) => !this.customizationExists(c));

    // Process this small batch
    Promise.all(
      batch.map((customization) => this.createNewShopGridItem(customization)),
    ).then(async () => {
      // If more items to process
      if (endIndex < itemsToCreate.length) {
        // Use setTimeout to yield to browser UI thread before next batch
        await waitForSeconds(this.scene.time, 0.1);
        this.processBatchedItems(
          itemsToCreate,
          displayCustomizations,
          batchSize,
          endIndex,
          onFinished,
        );
      } else {
        // All done
        this.items.forEach((i: ShopGridItem) => {
          i.setVisible(
            displayCustomizations.find((c) => c.id === i.cosmetic?.id) !==
              undefined,
          );
        });

        await waitForSeconds(this.scene.time, 0.1);

        this.loadingContainer?.setVisible(false);
        // Update the grid with existing items immediately
        if (this.itemGrid) {
          this.sortItems();
          this.itemGrid.elements = this.items.filter((i) => i.visible);
          this.itemGrid.updateLayout();
          if (this.scrollGroup) {
            this.scrollGroup.updateContentParameters();
            this.scrollGroup.backToTop();
          }
          this.updateInteractivityWindows();
          onFinished?.();
          this.isLoading = false;
        }
      }
    });
  }

  async createNewShopGridItem(
    customization: Customization,
  ): Promise<ShopGridItem> {
    const newItem = new ShopGridItem(this.scene, 0, 0);
    const gridMatrix = this.gridBacking.getWorldTransformMatrix();
    const point = gridMatrix.transformPoint(
      this.gridBacking.x,
      this.gridBacking.y,
    );
    newItem.setupInteraction(
      new Phaser.Geom.Rectangle(point.x, point.y, 128, 128),
    );
    await newItem.setCosmeticData(customization);
    newItem.setPriceVisible(this.showItemPrices);
    newItem.onShopItemClicked = (item: ShopGridItem) => {
      this.onUserClickedItem?.(item);
    };
    new ScrollGroupItem(
      this.scrollGroup,
      [newItem.shopGridItemBacking, newItem.shopGridItemBacking_m],
      () => {
        newItem.onShopItemClicked?.(newItem);
        PhaserAudioManager.playSFX(this.scene, 'submit-click');
      },
    );
    // newItem.onShopItemScrolled = (event: any) => {
    //   this.scrollGroup!.handleScroll(event);
    // };

    this.itemGrid!.addItem(newItem);
    this.items.push(newItem);
    newItem.setVisible(false);
    return newItem;
  }

  customizationExists(customization: Customization) {
    return (
      this.items.find((item) => item.cosmetic?.id === customization.id) !==
      undefined
    );
  }

  sortItems() {
    this.items.sort(this.sortPredicate);
  }

  updateInteractivityWindows() {
    this.scrollGroup.updateGraphicMask();
    this.items.forEach((item: ShopGridItem) => {
      item.setInteractivityWindow(this.scrollGroup.maskRect!);
    });
  }
}
